import * as React from 'react';
import { useLocation } from 'react-router';
import type { PiletApi } from '@chess/platform-web';
import { ANALYTICS_API, type Analytics } from '@chess/api-analytics';
import { LOG_API, type Log } from '@chess/api-log';
import { STORAGE_API, type Storage } from '@chess/api-storage';
import { SYNC_API, type SyncService } from '@chess/api-sync';
import { REVIEW_API, type Review } from '@chess/api-review';
import { measurementIdFor, pagePathOf, debugWanted } from './sites.ts';
import { startGtag, updateConsent } from './gtag.ts';
import { installClicks } from './click.ts';
import { CONSENT_PATH, consentRecord, decide, parseConsent, type ConsentRecord, type Decision } from './consent.ts';
import { fetchCountry } from './country.ts';
import { consentRequiredFor } from './regions.ts';
import { reviewStep, syncStep, type ReviewMemory, type SyncMemory } from './watchers.ts';
import { ConsentOverlay } from './ConsentOverlay.tsx';
import { PrivacyForm, type PrivacyAvailability, type PrivacyState, type SaveStatus } from './PrivacyForm.tsx';

// Provides `analytics` over Google Analytics 4, and sends everything the app
// counts, by listening: no other plugin calls it. Three gates, each a no-op
// provider (`enabled()` false, nothing loaded, nothing queued): no stream in
// the hostname map (`sites.ts`: the dev site, localhost, Storybook and the
// headless checks), a privacy signal (Do Not Track or Global Privacy
// Control), and the consent decision. Where a stream exists and no signal
// is set, the consent record and the visitor's country are read together
// and `decide` (`consent.ts`) says what happens: a grant starts the tag, a
// denial leaves the no-op, and without a record the sheet asks where consent
// is required first (`regions.ts`: the EEA, the UK, Switzerland, and
// anywhere unknown) while elsewhere analytics starts at once. The Privacy
// section of Settings shows the state and is the way to turn it off, or on
// again, wherever the visitor is.

export function setup(app: PiletApi) {
  const brand = app.tryGetApi('brand');
  const log = app.getApi<Log>(LOG_API).then((l) => l.child(app.meta.name));
  const hostname = location.hostname;
  const id = measurementIdFor(hostname);
  const nav = navigator as Navigator & { globalPrivacyControl?: boolean };
  const declined = nav.doNotTrack === '1' || nav.globalPrivacyControl === true;
  const availability: PrivacyAvailability = !id ? 'no-stream' : declined ? 'declined' : 'available';
  const eligible = availability === 'available';

  // The provider hands every call to the live tag once there is one; before,
  // and after a withdrawal, nothing happens. The last page sent is
  // remembered, so the first page view after the tag starts is not a second
  // one for the gate's route.
  let tag: Analytics | undefined;
  let live: Analytics | undefined;
  let sentPath: string | undefined;
  const analytics: Analytics = {
    page(path) {
      if (!live) return;
      live.page(path);
      sentPath = path;
    },
    track(event, params) {
      live?.track(event, params);
    },
    enabled: () => !!live,
  };
  app.registerApi(ANALYTICS_API, analytics);
  void log.then((l) => l.debug('analytics', { hostname, stream: !!id, declined, on: eligible }));

  // What the sheet and the Settings section watch: one small external store.
  interface State {
    prompt: boolean;
    decision?: Decision;
    record?: ConsentRecord;
    required?: boolean;
  }
  let state: State = { prompt: false };
  const listeners = new Set<() => void>();
  const set = (patch: Partial<State>) => {
    state = { ...state, ...patch };
    for (const l of listeners) l();
  };
  const useState = () =>
    React.useSyncExternalStore(
      (l) => {
        listeners.add(l);
        return () => listeners.delete(l);
      },
      () => state,
      () => state,
    );

  const start = () => {
    if (live) return;
    if (!tag && id) {
      let stored: string | null = null;
      try {
        stored = localStorage.getItem('ga_debug');
      } catch {
        stored = null;
      }
      tag = startGtag(id, { debug: debugWanted(location.search, stored) });
    } else {
      updateConsent(true);
    }
    live = tag;
    void log.then((l) => l.debug('analytics on', { hostname, stream: id }));
    const path = pagePathOf(location.pathname);
    if (sentPath !== path) analytics.page(path);
  };
  const stop = () => {
    if (!live) return;
    live = undefined;
    updateConsent(false);
    void log.then((l) => l.debug('analytics off', { hostname, stream: id }));
  };

  const storage = app.getApi<Storage>(STORAGE_API);
  const write = (consent: 'granted' | 'denied') => {
    const region = state.required === false ? 'free' : 'required';
    const text = consentRecord(consent, region);
    set({ record: parseConsent(text), decision: consent === 'granted' ? 'on' : 'off', prompt: false });
    return storage.then((s) => s.write(CONSENT_PATH, text));
  };
  /** The switch, and the sheet's Accept: a grant starts the tag, a denial stops it; the record is written after. */
  const choose = (granted: boolean) => {
    if (!eligible) return Promise.resolve();
    if (granted) start();
    else stop();
    return write(granted ? 'granted' : 'denied');
  };

  // The Settings section, on every host: where analytics cannot run it says
  // why, with the switch disabled.
  const PrivacySection: React.FC = () => {
    const s = useState();
    const [status, setStatus] = React.useState<SaveStatus>('idle');
    const [error, setError] = React.useState<string | undefined>();
    let privacy: PrivacyState | undefined;
    if (!eligible) privacy = 'off';
    else if (s.decision === 'on') privacy = s.record ? 'on' : 'implied';
    else if (s.decision) privacy = 'off';
    const onChange = (next: boolean) => {
      setStatus('saving');
      setError(undefined);
      choose(next).then(
        () => setStatus('saved'),
        (e) => {
          setStatus('error');
          setError(e instanceof Error ? e.message : String(e));
          void log.then((l) => l.error('consent could not be saved', e));
        },
      );
    };
    return <PrivacyForm availability={availability} state={privacy} status={status} error={error} onChange={onChange} />;
  };
  app.registerExtension('settings.sections', PrivacySection, { order: 26 });

  if (!eligible || !id) return;

  // The decision: the record and the country together, then one of three.
  // Nothing is sent before it; the first screen's page view goes when the
  // tag starts.
  const record = storage
    .then((s) => s.readText(CONSENT_PATH))
    .then(parseConsent)
    .catch((e) => {
      void log.then((l) => l.error('consent could not be read', e));
      return undefined;
    });
  void Promise.all([record, fetchCountry()]).then(([rec, country]) => {
    const required = consentRequiredFor(country);
    const decision = decide({ record: rec, required });
    void log.then((l) => l.debug('consent', { country: country ?? 'unknown', required, record: rec?.consent ?? 'none', decision }));
    set({ record: rec, required, decision, prompt: decision === 'ask' });
    if (decision === 'on') start();
  });
  const accept = () => {
    void choose(true).catch((e) => void log.then((l) => l.error('consent could not be saved', e)));
  };
  const Consent: React.FC = () => {
    const s = useState();
    if (!s.prompt) return null;
    return <ConsentOverlay brandName={brand.name} onAccept={accept} onDismiss={() => set({ prompt: false })} />;
  };
  app.registerExtension('shell.overlays', Consent);

  // A page view on every route change; draws nothing.
  const PageViews: React.FC = () => {
    const { pathname } = useLocation();
    React.useEffect(() => analytics.page(pagePathOf(pathname)), [pathname]);
    return null;
  };
  app.registerExtension('shell.gate', PageViews);

  // Clicks on controls, the whole document.
  installClicks(analytics);

  // The sync's completions, once each, with what came in.
  void app.getApi<SyncService>(SYNC_API).then((sync) => {
    let memory: SyncMemory = { done: sync.state().phase === 'done' };
    sync.subscribe(() => {
      const step = syncStep(memory, sync.state());
      memory = step.memory;
      if (step.event) analytics.track('sync_completed', step.event);
    });
  });

  // A review opened, once per game; no id leaves.
  void app.getApi<Review>(REVIEW_API).then((review) => {
    let memory: ReviewMemory = { ready: false };
    review.subscribe((state) => {
      const step = reviewStep(memory, state);
      memory = step.memory;
      if (step.opened) analytics.track('review_opened');
    });
  });

  // Puzzles: solved or failed; the move stays.
  app.on('review.puzzleSolved', () => analytics.track('puzzle_solved'));
  app.on('review.puzzleFailed', () => analytics.track('puzzle_failed'));
}
